/**
 * QA Pass Questions Component
 * Displays final QA checklist with validation status
 * Addresses Gap 8 requirements from gap analysis
 */

import React, { useState, useMemo } from 'react';
import { Card, Button, Badge, Checkbox } from '@tracekit/core';
import { CheckCircle, XCircle, AlertCircle, FileCheck, Calendar, FileText, ListChecks } from 'lucide-react';
import type { QAPassQuestion } from '../types/template';

interface QAPassQuestionsProps {
  questions: QAPassQuestion[] | string[];
  onQuestionComplete?: (questionIndex: number, isComplete: boolean) => void;
  readOnly?: boolean;
}

export function QAPassQuestions({
  questions,
  onQuestionComplete,
  readOnly = false,
}: QAPassQuestionsProps) {
  const [completedQuestions, setCompletedQuestions] = useState<Set<number>>(new Set());
  const [filterCategory, setFilterCategory] = useState<string>('all');

  // Normalize questions (handle both string and object formats)
  const normalizedQuestions = useMemo(() => {
    return questions.map((q, idx) => {
      if (typeof q === 'string') {
        return {
          question: q,
          category: categorizeQuestion(q),
          validation_type: getValidationType(q),
        };
      }
      return {
        question: q.question || '',
        category: q.category || categorizeQuestion(q.question || ''),
        validation_type: q.validation_type || getValidationType(q.question || ''),
      };
    });
  }, [questions]);

  const handleToggleComplete = (index: number) => {
    if (readOnly) return;
    
    const newCompleted = new Set(completedQuestions);
    if (newCompleted.has(index)) {
      newCompleted.delete(index);
    } else {
      newCompleted.add(index);
    }
    setCompletedQuestions(newCompleted);
    
    if (onQuestionComplete) {
      onQuestionComplete(index, newCompleted.has(index));
    }
  };

  const completionStats = useMemo(() => {
    const total = normalizedQuestions.length;
    const completed = completedQuestions.size;
    const byCategory = {
      format: normalizedQuestions.filter((q, idx) => q.category === 'format' && completedQuestions.has(idx)).length,
      completeness: normalizedQuestions.filter((q, idx) => q.category === 'completeness' && completedQuestions.has(idx)).length,
      compliance: normalizedQuestions.filter((q, idx) => q.category === 'compliance' && completedQuestions.has(idx)).length,
      quality: normalizedQuestions.filter((q, idx) => q.category === 'quality' && completedQuestions.has(idx)).length,
    };
    return { total, completed, byCategory, percentage: total > 0 ? (completed / total) * 100 : 0 };
  }, [normalizedQuestions, completedQuestions]);

  const filteredQuestions = useMemo(() => {
    if (filterCategory === 'all') return normalizedQuestions;
    return normalizedQuestions.filter(q => q.category === filterCategory);
  }, [normalizedQuestions, filterCategory]);

  if (!questions || questions.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <ListChecks className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-theme-text-secondary">
            No QA pass questions available. Generate a template first.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text flex items-center gap-2">
            <ListChecks className="w-5 h-5" />
            Final QA Pass (RAG-driven)
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Verify final assembly compliance before submission
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={completionStats.percentage === 100 ? 'success' : 'default'}>
            {completionStats.completed}/{completionStats.total} Complete
          </Badge>
          {completionStats.percentage > 0 && (
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {completionStats.percentage.toFixed(0)}%
            </span>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all duration-300"
            style={{ width: `${completionStats.percentage}%` }}
          />
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
        <Button
          variant={filterCategory === 'all' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setFilterCategory('all')}
        >
          All ({normalizedQuestions.length})
        </Button>
        <Button
          variant={filterCategory === 'format' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setFilterCategory('format')}
          className="flex items-center gap-1"
        >
          <FileText className="w-3 h-3" />
          Format ({normalizedQuestions.filter(q => q.category === 'format').length})
        </Button>
        <Button
          variant={filterCategory === 'completeness' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setFilterCategory('completeness')}
          className="flex items-center gap-1"
        >
          <FileCheck className="w-3 h-3" />
          Completeness ({normalizedQuestions.filter(q => q.category === 'completeness').length})
        </Button>
        <Button
          variant={filterCategory === 'compliance' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setFilterCategory('compliance')}
          className="flex items-center gap-1"
        >
          <CheckCircle className="w-3 h-3" />
          Compliance ({normalizedQuestions.filter(q => q.category === 'compliance').length})
        </Button>
        <Button
          variant={filterCategory === 'quality' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setFilterCategory('quality')}
          className="flex items-center gap-1"
        >
          <AlertCircle className="w-3 h-3" />
          Quality ({normalizedQuestions.filter(q => q.category === 'quality').length})
        </Button>
      </div>

      {/* Questions List */}
      <div className="space-y-3">
        {filteredQuestions.map((qa, idx) => {
          const originalIdx = normalizedQuestions.indexOf(qa);
          const isCompleted = completedQuestions.has(originalIdx);
          return (
            <div
              key={originalIdx}
              className={`p-4 border rounded-lg transition-colors ${
                isCompleted
                  ? 'border-green-300 bg-green-50 dark:bg-green-900/10 dark:border-green-700'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50'
              }`}
            >
              <div className="flex items-start gap-3">
                {!readOnly && (
                  <Checkbox
                    checked={isCompleted}
                    onChange={() => handleToggleComplete(originalIdx)}
                    className="mt-1"
                  />
                )}
                {readOnly && (
                  <div className="mt-1">
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getCategoryBadge(qa.category || 'format')}`}>
                      {getCategoryIcon(qa.category || 'format')}
                      <span className="capitalize">{qa.category}</span>
                    </span>
                    {qa.validation_type && (
                      <Badge variant="outline" className="text-xs">
                        {qa.validation_type.replace('_', ' ')}
                      </Badge>
                    )}
                  </div>
                  <p className={`text-sm ${isCompleted ? 'line-through text-gray-500' : 'text-gray-900 dark:text-theme-text'}`}>
                    {originalIdx + 1}. {qa.question}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {completionStats.percentage === 100 && (
        <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2 text-green-800 dark:text-green-400">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">All QA checks complete! Proposal is ready for submission.</span>
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Categorize question based on content
 */
function categorizeQuestion(questionText: string): 'format' | 'completeness' | 'compliance' | 'quality' {
  const text = questionText.toLowerCase();
  if (text.includes('page limit') || text.includes('date format') || text.includes('format')) {
    return 'format';
  }
  if (text.includes('missing') || text.includes('complete') || text.includes('all')) {
    return 'completeness';
  }
  if (text.includes('m1') || text.includes('m2') || text.includes('m3') || text.includes('m4') || 
      text.includes('g1') || text.includes('g2') || text.includes('criterion')) {
    return 'compliance';
  }
  return 'quality';
}

/**
 * Get validation type from question text
 */
function getValidationType(questionText: string): string {
  const text = questionText.toLowerCase();
  if (text.includes('page')) return 'page_limit';
  if (text.includes('date')) return 'date_format';
  if (text.includes('m1') || text.includes('m2') || text.includes('m3') || text.includes('m4')) return 'mandatory_criteria';
  if (text.includes('r10') || text.includes('quality')) return 'quality_check';
  if (text.includes('constraint')) return 'constraints';
  return 'general';
}

/**
 * Get category icon
 */
function getCategoryIcon(category: string) {
  switch (category) {
    case 'format':
      return <FileText className="w-3 h-3" />;
    case 'completeness':
      return <FileCheck className="w-3 h-3" />;
    case 'compliance':
      return <CheckCircle className="w-3 h-3" />;
    case 'quality':
      return <AlertCircle className="w-3 h-3" />;
    default:
      return <FileCheck className="w-3 h-3" />;
  }
}

/**
 * Get category badge color
 */
function getCategoryBadge(category: string) {
  const colors = {
    format: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    completeness: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    compliance: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    quality: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
  };
  return colors[category as keyof typeof colors] || colors.format;
}
